import { useState, useEffect, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { damageTypesReport } from '@/mocks/searchData';
import { useAuth } from '@/hooks/useAuth';

export default function Report() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [siteName, setSiteName] = useState('');
  const [domain, setDomain] = useState('');
  const [damageType, setDamageType] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [reporterEmail, setReporterEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMsg('');

    if (!siteName.trim() || !domain.trim() || !damageType) {
      setErrorMsg('사이트명, 도메인, 피해 유형은 필수 입력 항목입니다.');
      return;
    }
    if (description.trim().length > 500) {
      setErrorMsg('상세 내용은 500자를 초과할 수 없습니다.');
      return;
    }
    if (reporterEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(reporterEmail.trim())) {
      setErrorMsg('올바른 이메일 형식이 아닙니다.');
      return;
    }

    const formData = new FormData(e.currentTarget);
    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setStatus('success');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch('https://readdy.ai/api/form/d93v1ok41f1vs39a6kmg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          siteName: siteName.trim(),
          domain: domain.trim(),
          damageType,
          amount: amount.trim(),
          description: description.trim(),
          reporterEmail: reporterEmail.trim(),
        }).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore parse error */ }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (!response.ok || (parsed.code && parsed.code !== 'OK') || serverMsg.includes('spam')) {
        setErrorMsg(typeof serverMsg === 'string' ? serverMsg : '서버 오류가 발생했습니다. 다시 시도해주세요.');
        setStatus('error');
      } else {
        setSiteName('');
        setDomain('');
        setDamageType('');
        setAmount('');
        setDescription('');
        setReporterEmail('');
        setStatus('success');
      }
    } catch {
      setErrorMsg('네트워크 오류가 발생했습니다. 다시 시도해주세요.');
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Header />

      <main className="flex-1">
        <div className="bg-secondary-950">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">피해 제보하기</h1>
            <p className="text-sm text-foreground-400">
              먹튀 피해를 입으신 경우 아래 양식을 통해 제보해 주시면 신속히 검토하겠습니다
            </p>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 md:px-6 py-10">
          {status === 'success' ? (
            <div className="bg-white rounded-lg border border-foreground-200 p-10 text-center">
              <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-check-line text-3xl text-primary-500" />
              </div>
              <h2 className="text-xl font-bold text-foreground-950 mb-2">제보가 접수되었습니다</h2>
              <p className="text-sm text-foreground-600 mb-8 leading-relaxed">
                소중한 제보 감사드립니다. 검증팀에서 내용을 확인한 후<br />
                검증 결과를 등록하고 필요한 경우 연락드리겠습니다.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/search"
                  className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-md transition-colors whitespace-nowrap"
                >
                  검증 결과 확인하기
                </Link>
                <Link
                  to="/"
                  className="px-6 py-3 border border-foreground-200 hover:bg-background-100 text-foreground-700 text-sm font-medium rounded-md transition-colors whitespace-nowrap"
                >
                  메인으로 돌아가기
                </Link>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-foreground-200 p-8">
              <div className="flex items-start gap-4 mb-8 p-4 bg-primary-50 rounded-md border border-primary-100">
                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-information-line text-primary-600" />
                </div>
                <div className="text-sm text-primary-700 leading-relaxed">
                  <p className="font-medium mb-1">제보 시 유의사항</p>
                  <ul className="list-disc pl-4 space-y-0.5">
                    <li>허위 제보는 법적 책임을 질 수 있습니다</li>
                    <li>증거 자료(입금 내역, 대화 캡처 등)는 가능한 상세히 기재해주세요</li>
                    <li>제보 내용은 검증 후 익명으로 공개될 수 있습니다</li>
                  </ul>
                </div>
              </div>

              <form onSubmit={handleSubmit} data-readdy-form noValidate>
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="hp-field"
                />

                <div className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="siteName" className="block text-sm font-medium text-foreground-700 mb-1">
                        사이트명 <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="siteName"
                        name="siteName"
                        type="text"
                        value={siteName}
                        onChange={(e) => setSiteName(e.target.value)}
                        placeholder="예: 카지노킹"
                        className="w-full px-4 py-3 text-sm rounded-md border border-foreground-200 bg-white text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="domain" className="block text-sm font-medium text-foreground-700 mb-1">
                        도메인 <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="domain"
                        name="domain"
                        type="text"
                        value={domain}
                        onChange={(e) => setDomain(e.target.value)}
                        placeholder="예: casino-king.com"
                        className="w-full px-4 py-3 text-sm rounded-md border border-foreground-200 bg-white text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="damageType" className="block text-sm font-medium text-foreground-700 mb-1">
                        피해 유형 <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="damageType"
                        name="damageType"
                        value={damageType}
                        onChange={(e) => setDamageType(e.target.value)}
                        className="w-full px-4 py-3 text-sm rounded-md border border-foreground-200 bg-white text-foreground-950 focus:outline-none focus:border-primary-500 transition-colors cursor-pointer"
                      >
                        <option value="">피해 유형을 선택하세요</option>
                        {damageTypesReport.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="amount" className="block text-sm font-medium text-foreground-700 mb-1">
                        피해 금액 (만원)
                      </label>
                      <input
                        id="amount"
                        name="amount"
                        type="text"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="예: 100"
                        className="w-full px-4 py-3 text-sm rounded-md border border-foreground-200 bg-white text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="reporterEmail" className="block text-sm font-medium text-foreground-700 mb-1">
                      연락처 이메일
                    </label>
                    <input
                      id="reporterEmail"
                      name="reporterEmail"
                      type="email"
                      value={reporterEmail}
                      onChange={(e) => setReporterEmail(e.target.value)}
                      placeholder="연락 받으실 이메일을 입력하세요 (선택)"
                      className="w-full px-4 py-3 text-sm rounded-md border border-foreground-200 bg-white text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                    />
                  </div>

                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-foreground-700 mb-1">
                      상세 내용
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="피해 상황을 최대한 자세히 설명해주세요 (입금 내역, 대화 내용, 일시 등)"
                      rows={6}
                      maxLength={500}
                      className="w-full px-4 py-3 text-sm rounded-md border border-foreground-200 bg-white text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-500 transition-colors resize-none"
                    />
                    <p className="text-xs text-foreground-400 mt-1 text-right">
                      {description.length} / 500자
                    </p>
                  </div>

                  {(errorMsg || status === 'error') && (
                    <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-md">
                      {errorMsg}
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium text-sm py-3 rounded-md transition-colors whitespace-nowrap"
                    >
                      {submitting ? '제출 중...' : '제보하기'}
                    </button>
                    <Link
                      to="/"
                      className="flex-1 text-center border border-foreground-200 hover:bg-background-100 text-foreground-700 font-medium text-sm py-3 rounded-md transition-colors whitespace-nowrap"
                    >
                      취소
                    </Link>
                  </div>
                </div>
              </form>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}